import { CheckCircle2, CalendarClock, AlertTriangle, Clock3, OctagonAlert, Archive } from "lucide-react";

// Single source of truth for the DriveDesk Hub v1.1 unified 6-state status
// color system. Every status shown anywhere in the app (calendar, lessons,
// payments, notifications, vehicle issues, reports, forms, expenses) maps
// to one of these semantic buckets so color usage stays consistent and
// never relies on color alone (icon + label are always included alongside).
export const SEMANTIC_BUCKETS = {
  good_completed: {
    label: "Good",
    icon: CheckCircle2,
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    leftBorder: "border-l-emerald-500",
    rowTint: "bg-emerald-50/40",
  },
  scheduled_upcoming: {
    label: "Scheduled",
    icon: CalendarClock,
    // 815 Digital Services rebrand: "Scheduled" maps to the brand's gold
    // token (--status-scheduled, an alias of --primary) instead of blue,
    // per design_guidelines.md — this bucket is the highest-impact blue
    // remnant since StatusBadge/getStatusSemantic use it everywhere
    // (calendar, lessons list, dashboards, reports).
    badge: "bg-[hsl(var(--status-scheduled)/0.12)] text-[hsl(var(--status-scheduled))] border-[hsl(var(--status-scheduled)/0.3)]",
    dot: "bg-[hsl(var(--status-scheduled))]",
    leftBorder: "border-l-[hsl(var(--status-scheduled))]",
    rowTint: "bg-[hsl(var(--status-scheduled)/0.08)]",
  },
  needs_attention: {
    label: "Needs attention",
    icon: AlertTriangle,
    badge: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    leftBorder: "border-l-amber-500",
    rowTint: "bg-amber-50/40",
  },
  waiting: {
    label: "Waiting",
    icon: Clock3,
    badge: "bg-orange-50 text-orange-800 border-orange-200",
    dot: "bg-orange-500",
    leftBorder: "border-l-orange-500",
    rowTint: "bg-orange-50/40",
  },
  urgent: {
    label: "Urgent",
    icon: OctagonAlert,
    badge: "bg-rose-50 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
    leftBorder: "border-l-rose-500",
    rowTint: "bg-rose-50/40",
  },
  archived_inactive: {
    label: "Archived",
    icon: Archive,
    badge: "bg-zinc-100 text-zinc-700 border-zinc-200",
    dot: "bg-zinc-400",
    leftBorder: "border-l-zinc-400",
    rowTint: "bg-zinc-50",
  },
};

// Maps every domain-specific status value used across DriveDesk Hub to one
// of the 6 semantic buckets above.
export const STATUS_TO_BUCKET = {
  // lessons / appointments
  scheduled: "scheduled_upcoming",
  confirmed: "scheduled_upcoming",
  in_progress: "waiting",
  completed: "good_completed",
  no_show: "urgent",
  cancelled: "archived_inactive",
  rescheduled: "needs_attention",
  // vehicles
  available: "good_completed",
  in_use: "waiting",
  in_service: "needs_attention",
  maintenance: "needs_attention",
  out_of_service: "urgent",
  // payments
  paid: "good_completed",
  partial: "needs_attention",
  pending: "waiting",
  due: "urgent",
  past_due: "urgent",
  refunded: "archived_inactive",
  waived: "archived_inactive",
  on_hold: "urgent",
  // vehicle issues
  open: "needs_attention",
  in_review: "waiting",
  resolved: "good_completed",
  // generic priority / severity
  low: "archived_inactive",
  medium: "needs_attention",
  high: "urgent",
  // expenses
  approved: "good_completed",
  rejected: "urgent",
  reimbursed: "good_completed",
  // forms
  draft: "waiting",
  submitted: "waiting",
  needs_correction: "needs_attention",
  signed: "good_completed",
  expired: "urgent",
  void: "archived_inactive",
  // active/inactive generic
  active: "good_completed",
  inactive: "archived_inactive",
  // v1.3: classroom roster — attendance
  present: "good_completed",
  absent: "urgent",
  excused: "needs_attention",
  not_recorded: "archived_inactive",
  // v1.3: classroom roster — payment/forms indicators
  in_good_standing: "good_completed",
  complete: "good_completed",
  missing: "needs_attention",
};

export function getStatusSemantic(status) {
  const bucketKey = STATUS_TO_BUCKET[status] || "archived_inactive";
  return { bucketKey, ...SEMANTIC_BUCKETS[bucketKey] };
}
